/**
 * The editor's wiring: which reads a form needs, where the draft lives, and every write it makes.
 *
 * It decides nothing. `model/editorDraft` owns what a trainer may do to a form, `model/cadenceForm`
 * owns the rhythm's three rules, `model/editorPrompts` words the two questions — everything here is
 * about WHEN things are read and which route a write goes down.
 *
 * ── Three reads, one gate ────────────────────────────────────────────────────────────────────
 * The templates LIST, the form itself, and the standard catalog. The list is read for one fact the
 * detail routes cannot answer on their own: whether this id is the intake. That matters twice over,
 * because the intake is read and written through routes of its own —
 *
 * · **Read.** `GET /survey/template` resolves the trainer's intake without being told which one,
 *   which is also how a trainer who has never customised it is served the system default rather
 *   than a 404.
 * · **Write.** `PATCH /survey/template` UPSERTS that owned row. `PATCH /survey/template/{id}` loads
 *   `GetTrackedTemplateByIdForOwnerAsync`, so the id of a system default nobody owns yet would 404 —
 *   the exact case a trainer opening their intake for the first time is in.
 *
 * A check-in goes down the by-id route for both. The screen waits for all three reads before it
 * draws a field, because a form half-hydrated from the list would show a title and then grow its
 * questions under the trainer's pointer.
 *
 * ── The EXPOSED state is the wait branch; HYDRATION is not ──────────────────────────────────
 * `state` says „pending" for as long as the gate says the wait belongs on screen, which outlasts the
 * three reads by the skeleton's 300ms minimum life. The screen forks on it and so forks on the gate,
 * and the form that replaces the bars carries the reveal in its first render.
 *
 * The verdict the READS give is `answer`, and it is not exposed — but it is what the hydration
 * effect keys on, deliberately. Keying hydration on the gated state would hold the draft back for
 * the whole tail and land it AFTER the skeleton left, so the trainer would get one frame of an
 * editor with no form in it. The draft is built the moment the reads answer; only the bars wait.
 *
 * ── A SAVE THAT LANDS RETURNS TO THE LIST ────────────────────────────────────────────────────
 * Owner, 17 September 2026. Every successful write of the form — a PATCH on a stored one, a POST
 * from `/raporty/nowa` — ends on `/raporty`. „Zapisz i wyjdź" is the exception, because there the
 * trainer named a destination of their own; „Duplikuj ankietę" is the other, because a copy is
 * something to edit rather than something just finished. The POST's redirect `replace`s, so Back
 * does not return to an empty draft.
 *
 * ── The draft is hydrated ONCE per form ──────────────────────────────────────────────────────
 * A refetch landing mid-edit must not overwrite what the trainer has typed, so hydration is keyed by
 * the form's identity and runs when that changes — not whenever the query's data object does. After
 * a save the server's own response is hydrated back in, which is what makes the draft clean again
 * and what picks up the ids the backend minted for new questions.
 *
 * ── A WRITE'S ANSWER IS NOT AUTOMATICALLY THE DRAFT ──────────────────────────────────────────
 * Two of the three writes on this screen do not carry the form at all, and every one of them answers
 * with the STORED template — the one without whatever is currently being edited. So:
 *
 * · A full save goes through `adopt`, which takes the server's answer only when nothing has been
 *   dispatched since the request was sent, and otherwise keeps the trainer's keystrokes and only
 *   takes the id. `model/editorDraft` holds the rule; the reference comparison is what makes it
 *   exact.
 * · The pause writes ONE field and dispatches ONE field back. Hydrating from its response is how an
 *   entire unsaved form disappears with no dialog, which is the defect this shape exists to prevent.
 *
 * ── The guard lets THIS hook's own navigations through ───────────────────────────────────────
 * `leavingFor` is armed synchronously, immediately before every `navigate` the hook issues, and
 * `shouldBlockFn` reads it live. An earlier revision mirrored it into a second ref written from an
 * effect: the first save of a new form armed nothing until a render had passed, so the editor's own
 * redirect was blocked by its own guard and a second press of „Zapisz zmiany" created a SECOND
 * template on a live backend.
 *
 * ── A new form FOR a client is assigned, then returns to that client ─────────────────────────
 * `/raporty/nowa?klient=<id>` (8 October 2026, the client card's „Utwórz nową ankietę"). The
 * create body carries no recipients, so the first save POSTs the form, ADOPTS the answer — the
 * draft now holds the minted id, so a later „Zapisz zmiany" PATCHes rather than creating a twin —
 * and POSTs the assignment. Success lands on the client's Ankiety tab, `replace`d like the list's
 * redirect. A refused assignment keeps the trainer HERE, on the saved form, with a banner that says
 * the form exists and a retry: leaving would hide the failure on a screen that cannot explain it.
 */

import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useBlocker, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { publicFormRoot } from '~/entities/public-form';
import {
  useDeleteSurveyTemplateId,
  useDeleteSurveyTemplateIdAssignmentsCustomerId,
  usePatchSurveyTemplate,
  usePatchSurveyTemplateId,
  usePostSurveyTemplate,
  usePostSurveyTemplateIdAssignments,
} from '~/shared/api/generated/endpoints/survey/survey';
import type {
  TrainWithMeModulesSurveysDtosResponsesSurveyTemplateResponse as SurveyTemplateResponse,
} from '~/shared/api/generated/model';
import { clientSurveysQueryKey } from '~/features/survey-report';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import type { ConfirmPrompt } from '~/shared/ui/ConfirmDialog';

import {
  clientFacesQueryOptions,
  intakeTemplateQueryOptions,
  reportsRoot,
  surveyCatalogQueryOptions,
  surveyTemplateQueryOptions,
  surveyTemplatesQueryOptions,
} from '../api/reportsQueries';
import type { ClientFace } from '../model/clientFaces';
import {
  deleteSurveyPrompt,
  duplicateDraft,
  editorDraftReducer,
  EMPTY_DRAFT,
  hydrateDraft,
  leaveEditorPrompt,
  newCheckInDraft,
  saveRefusal,
  saveRefusalMessage,
  toCreateBody,
  toPatchBody,
  type EditorAction,
  type EditorDraft,
  type SaveRefusal,
  type SurveyKindKey,
} from '~/features/survey-question-editor';
import { surveyGroups } from '../model/surveyList';

/** Which form the route is pointing at. */
export type EditorTarget =
  | {
      readonly kind: 'new';
      /** `?klient=`: the client the first save assigns the new check-in to. */
      readonly forClientId?: number | null;
    }
  | { readonly kind: 'stored'; readonly templateId: number | null; readonly rawId: string };

export type EditorState = 'pending' | 'error' | 'ready';

export interface SurveyEditorView {
  /** What the screen draws. „pending" covers the whole gated wait, not only the open reads. */
  readonly state: EditorState;
  readonly skeleton: boolean;
  readonly reveal: string;
  readonly draft: EditorDraft;
  readonly dispatch: (action: EditorAction) => void;
  readonly kind: SurveyKindKey;
  /** Who holds this form, and their faces. Empty for a form that does not exist yet. */
  readonly assignedIds: readonly number[];
  readonly faces: ReadonlyMap<number, ClientFace>;
  /** Why „Zapisz zmiany" is refused, or `null` when it may act. */
  readonly refusal: SaveRefusal | null;
  readonly saving: boolean;
  /** The banner under the top bar when a write came back refused. */
  readonly writeFailed: boolean;
  /**
   * `?klient=`'s client, named once the roster answers (`null` until then, or when the roster does
   * not know them); `null` as a whole without the parameter.
   */
  readonly forClient: { readonly name: string | null } | null;
  /** The assignment after a first save for `forClient`: in flight, refused, or neither. */
  readonly clientAssign: 'sending' | 'failed' | null;
  readonly retryClientAssign: () => void;
  readonly save: () => void;
  /** Why „Duplikuj ankietę" is refused, or `null` when it may act. */
  readonly duplicateRefusal: string | null;
  readonly duplicate: () => void;
  readonly togglePaused: () => void;
  readonly requestDelete: () => void;
  readonly assign: (customerId: number) => void;
  readonly unassign: (customerId: number) => void;
  /** The dialog standing in front of the editor, or `null`. One at a time, by construction. */
  readonly confirm: ConfirmPrompt | null;
  readonly onConfirm: () => void;
  readonly onCancelConfirm: () => void;
  readonly onAlternate: () => void;
  readonly onRetry: () => void;
}

/**
 * Whether the delete dialog is standing.
 *
 * The LEAVE dialog is not state at all — it is `blocker.status === 'blocked'`, read directly.
 * Mirroring it into state would be a setState inside an effect, which is the cascading render the
 * lint rule names and, worse, a second source of truth for one question.
 */
type Pending = { readonly kind: 'delete' };

export function useSurveyEditor(target: EditorTarget): SurveyEditorView {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const templates = useQuery(surveyTemplatesQueryOptions());
  const roster = useQuery(clientFacesQueryOptions());

  const storedId = target.kind === 'stored' ? target.templateId : null;
  const row = useMemo(() => {
    const groups = surveyGroups(templates.data, i18n.language);
    if (storedId === null) return null;
    if (groups.intake?.id === storedId) return groups.intake;
    if (groups.leadForm?.id === storedId) return groups.leadForm;
    return groups.checkIns.find((entry) => entry.id === storedId) ?? null;
  }, [templates.data, i18n.language, storedId]);

  // The public form's template (public-form R4) is edited here too, by id like a check-in, under
  // the lead form's own rules.
  const kind: SurveyKindKey =
    row?.leadForm === true ? 'leadForm' : row?.mandatory === true ? 'intake' : 'checkIn';

  const catalog = useQuery(surveyCatalogQueryOptions(kind));
  const intake = useQuery({ ...intakeTemplateQueryOptions(), enabled: kind === 'intake' && storedId !== null });
  const checkIn = useQuery(surveyTemplateQueryOptions(kind !== 'intake' ? storedId : null));
  const detail = kind === 'intake' ? intake : checkIn;

  const [draft, dispatch] = useReducer(editorDraftReducer, EMPTY_DRAFT);
  const [pending, setPending] = useState<Pending | null>(null);
  const [writeFailed, setWriteFailed] = useState(false);
  const forClientId = target.kind === 'new' ? (target.forClientId ?? null) : null;
  /** The created form's assignment to `forClientId`, once the POST has minted it. */
  const [clientAssign, setClientAssign] = useState<{
    readonly templateId: number;
    readonly status: 'sending' | 'failed';
  } | null>(null);

  /**
   * What the draft in hand was built from.
   *
   * Hydration is keyed by this rather than by the response object, so a background refetch cannot
   * throw away an edit in progress. A save writes the server's answer back through `adopt`, which
   * also moves this key — that is what makes the draft clean and picks up the ids the backend minted
   * for questions that had none.
   */
  const hydratedFrom = useRef<string | null>(null);

  const adopt = (
    template: SurveyTemplateResponse,
    nextKind: SurveyKindKey,
    sent: EditorDraft,
  ): void => {
    // The SAME key the hydration effect computes, so the refetch this save triggers does not
    // hydrate a second time over an answer we already hold.
    hydratedFrom.current = `${nextKind}:${String(template.id ?? 0)}`;
    dispatch({
      type: 'adopt',
      sent,
      saved: hydrateDraft({ kind: nextKind, template, catalog: catalog.data }),
    });
  };

  const readsPending =
    templates.isPending ||
    roster.isPending ||
    catalog.isPending ||
    (target.kind === 'stored' && (detail.isPending || row === null));
  const readsFailed =
    templates.isError ||
    roster.isError ||
    catalog.isError ||
    (target.kind === 'stored' && (detail.isError || (templates.isSuccess && row === null)));

  /**
   * What the READS say. Unexposed, and the tail is never folded into it: the hydration effect below
   * runs off this, so the draft is ready the moment the reads are — see the header.
   */
  const answer: EditorState =
    target.kind === 'stored' && target.templateId === null
      ? 'error'
      : readsFailed
        ? 'error'
        : readsPending
          ? 'pending'
          : 'ready';

  useEffect(() => {
    if (answer !== 'ready') return;
    const key =
      target.kind === 'new' ? 'new' : `${kind}:${String(detail.data?.id ?? storedId ?? 0)}`;
    if (hydratedFrom.current === key) return;
    hydratedFrom.current = key;
    dispatch({
      type: 'hydrate',
      draft:
        target.kind === 'new'
          ? newCheckInDraft(catalog.data)
          : hydrateDraft({ kind, template: detail.data, catalog: catalog.data }),
    });
  }, [answer, target.kind, kind, detail.data, storedId, catalog.data]);

  const gate = useSkeletonGate(answer === 'pending');
  const state: EditorState = gate.waiting ? 'pending' : answer;

  // ── Writes ─────────────────────────────────────────────────────────────────────────────────

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: reportsRoot });
    // The lead form's questions are the public form's too (public-form R4): the builder's cached
    // copy must not outlive a save made here.
    void queryClient.invalidateQueries({ queryKey: publicFormRoot });
  };

  const patchIntake = usePatchSurveyTemplate();
  const patchCheckIn = usePatchSurveyTemplateId();
  const create = usePostSurveyTemplate();
  const remove = useDeleteSurveyTemplateId();
  const addAssignment = usePostSurveyTemplateIdAssignments();
  const dropAssignment = useDeleteSurveyTemplateIdAssignmentsCustomerId();

  const saving =
    patchIntake.isPending ||
    patchCheckIn.isPending ||
    create.isPending ||
    remove.isPending ||
    clientAssign?.status === 'sending';

  /**
   * A navigation this hook is about to issue ITSELF, so the leave guard lets it through.
   *
   * Two of them exist: the copy's own address, and the LIST — which every save, and the delete,
   * return to. Written synchronously and read live by `shouldBlockFn` — see the header for the
   * defect that shape replaced.
   */
  const leavingFor = useRef<'copy' | 'list' | 'client' | null>(null);

  /**
   * „Zapisz i wyjdź": a navigation the guard is holding until the save it asked for has landed.
   *
   * Released on success only. A refused or failed save leaves the trainer exactly where they were,
   * with the banner saying why — carrying them out on an intention that did not survive is the data
   * loss the guard exists to prevent.
   */
  const leaveAfterSave = useRef(false);

  /** Leave for an address of this hook's own choosing, with the guard told first. See the header. */
  const leaveFor = (to: 'copy' | 'list' | 'client', go: () => Promise<void>): void => {
    leavingFor.current = to;
    void go().finally(() => {
      leavingFor.current = null;
    });
  };

  const afterWrite = (
    response: SurveyTemplateResponse,
    nextKind: SurveyKindKey,
    sent: EditorDraft,
  ): void => {
    setWriteFailed(false);
    invalidate();
    adopt(response, nextKind, sent);
    if (leaveAfterSave.current) {
      // „Zapisz i wyjdź": the trainer named a destination and it is not the list.
      leaveAfterSave.current = false;
      blockerRef.current.proceed?.();
      return;
    }
    // A save that lands RETURNS TO THE LIST — owner, 17 September 2026. The editor is entered from
    // `/raporty` and answers one question („what does this form ask?"); staying on it afterwards
    // left the trainer on a screen they were done with, and the list is where the saved form, its
    // cadence and its roster are read back. `adopt` above runs FIRST, so the draft is clean before
    // the guard is asked anything; `leaveFor` arms it anyway, because a save that raced a keystroke
    // keeps that keystroke and is therefore still dirty.
    if (forClientId !== null && clientAssign?.status === 'failed') {
      // The form was opened FOR a client and they still do not have it: this save is the retry.
      assignToClient(clientAssign.templateId, forClientId);
      return;
    }
    leaveFor('list', () => navigate({ to: '/raporty' }));
  };

  /** The new form to `?klient=`'s client — see the header. Success leaves for their Ankiety tab. */
  const assignToClient = (templateId: number, clientId: number): void => {
    setClientAssign({ templateId, status: 'sending' });
    addAssignment.mutate(
      { id: templateId, data: { customerIds: [clientId] } },
      {
        onSuccess: () => {
          setClientAssign(null);
          invalidate();
          void queryClient.invalidateQueries({ queryKey: clientSurveysQueryKey(clientId) });
          if (leaveAfterSave.current) {
            // „Zapisz i wyjdź": the trainer named a destination, and it is released only now.
            leaveAfterSave.current = false;
            blockerRef.current.proceed?.();
            return;
          }
          leaveFor('client', () =>
            navigate({
              to: '/klienci/$clientId',
              params: { clientId: String(clientId) },
              search: { tab: 'surveys' },
              replace: true,
            }),
          );
        },
        onError: () => {
          leaveAfterSave.current = false;
          blockerRef.current.reset?.();
          setClientAssign({ templateId, status: 'failed' });
        },
      },
    );
  };

  const submit = (next: EditorDraft, options?: { readonly asCopy?: boolean }): void => {
    setWriteFailed(false);
    const onError = (): void => {
      leavingFor.current = null;
      leaveAfterSave.current = false;
      blockerRef.current.reset?.();
      setWriteFailed(true);
    };

    if (next.templateId === null) {
      const asCopy = options?.asCopy === true;
      create.mutate(
        { data: toCreateBody(next) },
        {
          onSuccess: (response) => {
            setWriteFailed(false);
            invalidate();
            const id = response.id ?? null;
            if (id === null) {
              // No id to address: nothing to navigate to, so the answer is adopted where we stand.
              afterWrite(response, 'checkIn', next);
              return;
            }
            if (forClientId !== null && !asCopy) {
              adopt(response, 'checkIn', next);
              // `adopt` keys the draft by its new id; this screen's hydration is keyed `new`, and a
              // mismatch would re-seed a BLANK draft over the saved one on the next refetch.
              hydratedFrom.current = 'new';
              assignToClient(id, forClientId);
              return;
            }
            if (leaveAfterSave.current) {
              // „Zapisz i wyjdź" on a form that did not exist yet: the trainer named a destination
              // and it is not this form's own new address.
              leaveAfterSave.current = false;
              blockerRef.current.proceed?.();
              return;
            }
            // A COPY opens the copy — that is the whole of what „Duplikuj ankietę" is for, and the
            // original stays in history behind it. A first SAVE returns to the list, like every
            // other save on this screen (owner, 17 September 2026), and `replace` keeps
            // `/raporty/nowa` out of the history: an empty draft is not a place Back should land.
            leaveFor(asCopy ? 'copy' : 'list', () =>
              asCopy
                ? navigate({ to: '/raporty/$templateId', params: { templateId: String(id) } })
                : navigate({ to: '/raporty', replace: true }),
            );
          },
          onError,
        },
      );
      return;
    }

    if (next.kind === 'intake') {
      // The intake's own route, and not the by-id one: see the header.
      patchIntake.mutate(
        { data: { items: toPatchBody(next).items ?? [] } },
        { onSuccess: (response) => { afterWrite(response, 'intake', next); }, onError },
      );
      return;
    }

    patchCheckIn.mutate(
      { id: next.templateId, data: toPatchBody(next) },
      { onSuccess: (response) => { afterWrite(response, next.kind, next); }, onError },
    );
  };

  const refusal = saveRefusal(draft, { requireDirty: target.kind === 'stored' });

  // A dirty draft is also every refused draft — `saveRefusal` answers `clean` for a spotless one —
  // so one condition covers both cases the copy must not be taken from.
  const duplicateRefusal = draft.dirty ? t('web:reports.editor.menu.duplicateDirty') : null;

  const save = (): void => {
    if (refusal !== null) return;
    submit(draft);
  };

  // ── The leave guard ────────────────────────────────────────────────────────────────────────

  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) => {
      if (current.pathname === next.pathname) return false;
      // The hook's OWN redirect, armed synchronously before the `navigate` that follows it. Read
      // live rather than mirrored through a render — see the header.
      if (leavingFor.current !== null) return false;
      return true;
    },
    enableBeforeUnload: true,
    disabled: !draft.dirty,
    withResolver: true,
  });

  const blockerRef = useRef(blocker);
  useEffect(() => {
    blockerRef.current = blocker;
  });

  // The delete question outranks the leave one: a delete that is already in flight has taken the
  // trainer past the point where „stay or discard" is the question in front of them.
  const confirm: ConfirmPrompt | null =
    pending !== null
      ? deleteSurveyPrompt(draft, t, remove.isPending)
      : blocker.status === 'blocked'
        ? // „Zapisz i wyjdź" cannot act on a form the save itself refuses, and the refusal's own
          // sentence is behind the scrim while the dialog stands — so it travels onto the prompt.
          leaveEditorPrompt(t, saving, refusal === null ? null : saveRefusalMessage(refusal, t))
        : null;

  return {
    state,
    skeleton: gate.skeleton,
    reveal: gate.reveal,
    draft,
    dispatch,
    kind,
    assignedIds: detail.data?.assignedClients?.flatMap((client) =>
      typeof client.customerId === 'number' ? [client.customerId] : [],
    ) ?? row?.assignedCustomerIds ?? [],
    faces: roster.data ?? new Map(),
    refusal,
    saving,
    writeFailed,
    forClient:
      forClientId === null ? null : { name: roster.data?.get(forClientId)?.name ?? null },
    clientAssign: clientAssign?.status ?? null,
    retryClientAssign: () => {
      if (clientAssign?.status !== 'failed' || forClientId === null) return;
      assignToClient(clientAssign.templateId, forClientId);
    },
    save,
    duplicateRefusal,
    /**
     * „Duplikuj ankietę" — a copy of the SAVED form, never of the draft in hand.
     *
     * The menu offers one act and the trainer reads it as „another one like this one", meaning the
     * survey their clients have. Copying the draft carried whatever was half-typed into a new
     * template while leaving the original with none of it, and it did so without a dialog — so a
     * dirty form is refused with the reason instead, in the menu, before anything is posted.
     */
    duplicate: () => {
      if (duplicateRefusal !== null) return;
      const saved = hydrateDraft({ kind, template: detail.data, catalog: catalog.data });
      submit(
        duplicateDraft(saved, t('web:reports.editor.menu.copyTitle', { title: saved.title }), () =>
          crypto.randomUUID(),
        ),
        { asCopy: true },
      );
    },
    togglePaused: () => {
      if (draft.templateId === null || draft.kind !== 'checkIn') return;
      const next = !draft.active;
      setWriteFailed(false);
      patchCheckIn.mutate(
        { id: draft.templateId, data: { isActive: next } },
        {
          // ONE field in, one field back. The response carries the STORED template, so hydrating
          // from it here discards everything the trainer has typed and has not saved.
          onSuccess: () => { setWriteFailed(false); invalidate(); dispatch({ type: 'setActive', active: next }); },
          onError: () => { setWriteFailed(true); },
        },
      );
    },
    requestDelete: () => { if (draft.kind !== 'checkIn') return; setPending({ kind: 'delete' }); },
    /**
     * Assignment is a CHECK-IN's act on the wire.
     *
     * `AssignCheckInAsync` and `UnassignCheckInAsync` both refuse anything else outright — „Only a
     * check-in survey can be assigned this way." — and the intake is assigned by onboarding rather
     * than by a trainer. The form draws no control for it; the guard is here so no stray call can
     * reach an endpoint that is certain to answer 400.
     */
    assign: (customerId) => {
      if (draft.templateId === null || draft.kind !== 'checkIn') return;
      addAssignment.mutate(
        { id: draft.templateId, data: { customerIds: [customerId] } },
        { onSuccess: invalidate, onError: () => { setWriteFailed(true); } },
      );
    },
    unassign: (customerId) => {
      if (draft.templateId === null || draft.kind !== 'checkIn') return;
      dropAssignment.mutate(
        { id: draft.templateId, customerId },
        { onSuccess: invalidate, onError: () => { setWriteFailed(true); } },
      );
    },
    confirm,
    onConfirm: () => {
      if (pending !== null) {
        if (draft.templateId === null) return;
        remove.mutate(
          { id: draft.templateId },
          {
            onSuccess: () => {
              setPending(null);
              invalidate();
              leaveFor('list', () => navigate({ to: '/raporty' }));
            },
            onError: () => { setPending(null); setWriteFailed(true); },
          },
        );
        return;
      }
      // Leaving without saving: the edits go, which is what the prompt's danger confirm says.
      blockerRef.current.proceed?.();
    },
    onCancelConfirm: () => {
      setPending(null);
      blockerRef.current.reset?.();
    },
    onAlternate: () => {
      // „Zapisz i wyjdź". The held navigation is released only once the save has landed — carrying
      // the trainer out on an intention that failed is the data loss the guard exists to prevent.
      if (refusal !== null) return;
      setPending(null);
      leaveAfterSave.current = true;
      submit(draft);
    },
    onRetry: () => {
      void templates.refetch();
      void roster.refetch();
      void catalog.refetch();
      void detail.refetch();
    },
  };
}
